import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight } from 'lucide-react';

const references = [
  { name: 'Shell', industry: 'Akaryakıt', logo: '/images/logos/shell.webp', description: 'Global akaryakıt ve enerji şirketi' },
  { name: 'Petrol Ofisi', industry: 'Akaryakıt', logo: '/images/logos/petrol-ofisi.webp', description: 'Türkiye\'nin lider akaryakıt dağıtıcısı' },
  { name: 'TotalEnergies', industry: 'Enerji', logo: '/images/logos/total.webp', description: 'Uluslararası enerji ve petrol şirketi' },
  { name: 'Opet', industry: 'Akaryakıt', logo: '/images/logos/opet.webp', description: 'Türkiye akaryakıt dağıtım ve istasyon ağı' },
  { name: 'Lukoil', industry: 'Enerji', logo: '/images/logos/lukoil.webp', description: 'Uluslararası enerji ve petrol şirketi' },
  { name: 'Aytemiz', industry: 'Akaryakıt', logo: '/images/logos/aytemiz.webp', description: 'Türkiye akaryakıt dağıtım ağı' },
  { name: 'Alpet', industry: 'Akaryakıt', logo: '/images/logos/alpet.webp', description: 'Akaryakıt dağıtım ve istasyon ağı' },
];

const testimonials = [
  {
    quote: 'Asküçükler ile 10 yılı aşkın süredir çalışıyoruz. Güvenilirlik ve dakiklik konusunda hiç hayal kırıklığına uğramadık.',
    author: 'Mehmet Y.',
    position: 'Operasyon Direktörü',
    company: 'Shell',
  },
  {
    quote: 'Tehlikeli madde taşımacılığında güvenlik bizim için en önemli kriter. Asküçükler bu konuda sektörün en iyisi.',
    author: 'Ayşe K.',
    position: 'Lojistik Müdürü',
    company: 'Petrol Ofisi',
  },
  {
    quote: 'Modern filoları ve dijital takip sistemleri ile operasyonlarımızı çok daha verimli yönetebiliyoruz.',
    author: 'Can D.',
    position: 'Tedarik Zinciri Yöneticisi',
    company: 'TotalEnergies',
  },
];

const ReferencesPage = () => (
  <div data-testid="references-page" className="bg-white">
    <section className="page-hero-banner">
      <img src="/images/hero-slide-3.webp" alt="" className="hero-bg" loading="eager" />
      <div className="hero-gradient" />
      <div className="hero-inner">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="max-w-2xl">
          <span className="inline-block text-[#FF6B2C] text-xs font-semibold tracking-[0.2em] uppercase mb-4">Referanslarımız</span>
          <h1 className="heading-xl text-white mb-4">Uzun vadeli güven.</h1>
          <p className="text-base text-white/70 leading-relaxed max-w-lg">Sektörün lider firmalarıyla sürdürülebilir iş birlikleri.</p>
        </motion.div>
      </div>
    </section>

    <section className="section-gray section-padding">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
          <div className="section-label justify-center"><span>İş Ortaklarımız</span></div>
          <h2 className="heading-lg text-[#0B3A82]">Birlikte çalıştığımız firmalar.</h2>
        </motion.div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
          {references.map((ref, index) => (
            <motion.div key={ref.name} initial={{ opacity: 0, scale: 0.96 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: index * 0.06 }}
              className="bg-white border border-[#E2E8F0] rounded-2xl p-6 sm:p-8 flex flex-col items-center text-center hover:border-[#0B3A82]/20 hover:shadow-lg transition-all duration-300"
            >
              <div className="h-16 sm:h-20 flex items-center justify-center mb-5">
                <img src={ref.logo} alt={ref.name} className={`h-12 sm:h-16 w-auto object-contain`} loading="lazy" />
              </div>
              <h4 className="font-bold text-[#1A1A2E] text-sm mb-1">{ref.name}</h4>
              <p className="text-xs text-[#FF6B2C] font-semibold mb-1.5">{ref.industry}</p>
              <p className="text-xs text-[#94A3B8]">{ref.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    <section className="section-white section-padding">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
          <div className="section-label justify-center"><span>Müşteri Görüşleri</span></div>
          <h2 className="heading-lg text-[#0B3A82]">İş ortaklarımız ne diyor?</h2>
        </motion.div>
        <div className="grid md:grid-cols-3 gap-8">
          {testimonials.map((t, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              className="testimonial-card"
            >
              <p className="text-[#64748B] leading-relaxed mb-8 italic relative z-10 text-[0.9375rem]">"{t.quote}"</p>
              <div className="border-t border-[#E2E8F0] pt-5">
                <div className="font-bold text-[#1A1A2E]">{t.author}</div>
                <div className="text-sm text-[#64748B]">{t.position}</div>
                <div className="text-sm text-[#FF6B2C] font-semibold">{t.company}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    <section className="cta-section section-padding-sm">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center relative z-10">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <h2 className="heading-lg text-white mb-5">Siz de güvenilir bir iş ortağı arıyorsanız.</h2>
          <p className="text-white/70 max-w-2xl mx-auto mb-10 text-lg">Operasyonunuzu birlikte planlayalım.</p>
          <Link to="/iletisim">
            <Button className="bg-[#FF6B2C] hover:bg-[#E55A1F] text-white rounded-full font-semibold px-10 py-6 text-base shadow-lg group" data-testid="references-cta-button">
              Ekibimizle görüşün
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </motion.div>
      </div>
    </section>
  </div>
);

export default ReferencesPage;
